import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { setTokens } from '@/services/api';
import { AUTH_STORE_KEY, authPersistStorage } from '@/services/auth-storage';
import type { MyMembership } from '@/services/team';
// tenantStore is the single source of truth for the full tenant object. authStore
// only tracks the tenantId. Importing here is safe: tenantStore never imports
// authStore, so there is no circular dependency.
import { useTenantStore, type CrmTenantConfig } from './tenantStore';
import type { CrmPermissionMap } from '@/lib/crm-permissions';

/**
 * Mapa de permissoes do usuario, derivado do catalogo unico
 * (`lib/crm-permissions.ts`) em vez de reescrito a mao aqui.
 *
 * Ate esta fatia isto era uma interface com 20 campos escolhidos a dedo, que
 * ja tinha divergido da union `CrmPermission` do backend (33 chaves) e da
 * lista renderizada pela tela de cargos (15 chaves) — tres fontes da verdade
 * para a MESMA coisa. `canViewAuditLogs`, por exemplo, nao estava em nenhuma
 * das duas listas do frontend, entao a permissao existia no backend e era
 * impossivel de conceder pela UI.
 *
 * Agora ha uma so fonte: adicionar uma permissao no catalogo a inclui aqui
 * automaticamente, e o teste `__tests__/lib/crm-permissions.test.ts` garante
 * que o catalogo nao pode divergir do backend.
 */
export type UserPermissions = CrmPermissionMap;

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'USER' | 'manager' | 'sdr';
  avatar?: string;
  phone?: string;
  tenantId?: string;
  onboardingCompleted?: boolean;
  permissions?: UserPermissions;
}

export interface Admin {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'support';
}

/**
 * Tenant shape returned by the auth/checkout backend responses. It is a superset
 * of what the app stores: the id is kept in authStore (`tenantId`) while the full
 * object is mapped into the tenantStore (the single source of truth).
 */
export interface TenantInfo {
  id: string;
  name: string;
  slug?: string;
  plan?: string;
  clientUrl?: string;
  layoutType?: string;
  features?: Record<string, boolean>;
  onboardingStep?: number;
  onboardingSkipped?: boolean;
  tourCompleted?: boolean;
}

/**
 * Mapeia o tenant da resposta de auth/checkout para o formato do `tenantStore`.
 *
 * ## A marca NAO vem por aqui — e a linha que dizia que vinha era morta
 *
 * `getTenantInfo` do api-core seleciona `{ id, name, slug, clientUrl,
 * layoutType }` + features: nem `logoUrl`, nem `appName`, nem `primaryColor`.
 * O campo `logo` que este mapa lia nunca existiu na resposta (mesma familia do
 * `logo` × `logoUrl` que a story 11 achou em `services/settings.ts`), e o teste
 * que o cobria passava porque a fixture o inventava.
 *
 * Removida a linha, sobra o defeito que ela escondia: `setTenant` **substitui**
 * o tenant inteiro, entao entrar na conta apagava o logo, o nome exibido e a
 * cor que a tela de login ja tinha resolvido por `/public/tenant/resolve` — e
 * como `setTenant` marca `isResolved`, o `TenantProvider` nao resolvia de novo:
 * a marca so voltava num F5. Por isso a marca ja conhecida e' PRESERVADA, e so
 * quando o tenant e' o mesmo (carregar a marca de outro tenant seria vazamento).
 */
function tenantInfoToCrmConfig(
  tenant: TenantInfo,
  atual: CrmTenantConfig | null,
): CrmTenantConfig {
  const marcaJaResolvida = atual?.id === tenant.id ? atual : null;

  return {
    id: tenant.id,
    name: tenant.name,
    slug: tenant.slug || tenant.id,
    status: 'ACTIVE',
    layoutType: tenant.layoutType,
    clientUrl: tenant.clientUrl,
    features: tenant.features || {},
    appName: marcaJaResolvida?.appName,
    logoUrl: marcaJaResolvida?.logoUrl,
    faviconUrl: marcaJaResolvida?.faviconUrl,
    primaryColor: marcaJaResolvida?.primaryColor,
    accentColor: marcaJaResolvida?.accentColor,
    subdomain: marcaJaResolvida?.subdomain,
  };
}

interface AuthState {
  // User auth (client)
  user: User | null;
  // Only the tenant id lives here. The full tenant object is owned by
  // tenantStore (the single source of truth).
  tenantId: string | null;
  permissions: UserPermissions | null;

  // Team membership (CRM dynamic roles)
  teamMember: MyMembership | null;

  // Admin auth
  admin: Admin | null;

  // Common
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;

  // Actions
  setUser: (user: User) => void;
  setTenantId: (tenantId: string) => void;
  setPermissions: (permissions: UserPermissions) => void;
  setTeamMember: (member: MyMembership | null) => void;
  setAdmin: (admin: Admin) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (updates: Partial<User>) => void;
  setHasHydrated: (state: boolean) => void;

  // Auth from checkout
  setAuthFromCheckout: (user: User, accessToken: string, refreshToken: string | undefined, tenant?: TenantInfo) => void;

  // Permission helpers
  hasCrmPermission: (key: string) => boolean;
  isManager: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenantId: null,
      permissions: null,
      teamMember: null,
      admin: null,
      isAuthenticated: false,
      isAdmin: false,
      isLoading: false,
      _hasHydrated: false,

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },

      setUser: (user) => {
        set({
          user,
          permissions: user.permissions || null,
          teamMember: null,
          admin: null,
          isAuthenticated: true,
          isAdmin: false,
          isLoading: false,
        });
      },

      setTenantId: (tenantId) => {
        set({ tenantId });
      },

      setPermissions: (permissions) => {
        set({ permissions });
      },

      setTeamMember: (member) => {
        set({ teamMember: member });
      },

      setAdmin: (admin) => {
        set({
          admin,
          user: null,
          tenantId: null,
          permissions: null,
          teamMember: null,
          isAuthenticated: true,
          isAdmin: true,
          isLoading: false,
        });
        // Switching to an admin session zeroes the tenant context too — clear
        // the persisted tenantStore so no stale tenant survives (see logout).
        useTenantStore.getState().reset();
      },

      logout: () => {
        set({
          user: null,
          tenantId: null,
          permissions: null,
          teamMember: null,
          admin: null,
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
        });
        // Reset the persisted tenantStore too. Without this, logging into a
        // DIFFERENT account on the same browser keeps the previous tenant's id
        // in localStorage → api.ts sends a mismatched X-Tenant-Id (cross-tenant
        // leak) and the brand renders wrong until a hard reload.
        useTenantStore.getState().reset();
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setAuthFromCheckout: (user, accessToken, refreshToken, tenant) => {
        setTokens(accessToken, refreshToken);
        set({
          user,
          tenantId: tenant?.id ?? null,
          permissions: user.permissions || null,
          teamMember: null,
          admin: null,
          isAuthenticated: true,
          isAdmin: false,
          isLoading: false,
        });
        // Write the full tenant into the single source of truth (tenantStore).
        if (tenant) {
          const tenantStore = useTenantStore.getState();
          tenantStore.setTenant(tenantInfoToCrmConfig(tenant, tenantStore.tenant));
        }
      },

      updateUser: (updates) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      hasCrmPermission: (key: string) => {
        const { user, teamMember } = get();
        if (!user) return false;

        // Admin always has access
        if (user.role === 'ADMIN') return true;

        // If teamMember not loaded yet, deny access (secure by default)
        if (!teamMember) return false;

        // Owner or isOwner flag
        if (teamMember.isOwner) return true;

        const perms = teamMember.effectivePermissions;

        // ALL: true is wildcard
        if (perms?.ALL === true) return true;

        return perms?.[key] === true;
      },

      isManager: () => {
        const { user } = get();
        return user?.role === 'manager';
      },
    }),
    {
      name: AUTH_STORE_KEY,
      // "Lembrar de mim": localStorage ou sessionStorage conforme a escolha do
      // login (decidida em `services/auth-storage`, junto com os tokens).
      storage: createJSONStorage(() => authPersistStorage),
      partialize: (state) => ({
        user: state.user,
        tenantId: state.tenantId,
        permissions: state.permissions,
        teamMember: state.teamMember,
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
        isAdmin: state.isAdmin,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Hook to wait for hydration
export const useHasHydrated = () => {
  return useAuthStore((state) => state._hasHydrated);
};
